import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import SafeIcon from '../../common/SafeIcon';

const CartDrawer = () => {
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, cartTotal, clearCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleCheckout = () => {
    setIsCheckingOut(true);
    // Simulate API call
    setTimeout(() => {
      setIsCheckingOut(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setIsCartOpen(false);
        clearCart();
      }, 3000);
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-frost-dark/40 backdrop-blur-sm z-[60]"
          />
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-frost-vanilla z-[70] shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-frost-ice flex items-center justify-between">
              <h2 className="text-2xl font-serif font-bold text-frost-berry">Your Flight Box</h2>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-frost-ice rounded-full transition-colors"
                aria-label="Close cart"
              >
                <SafeIcon icon="FiX" className="text-xl" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto p-6 no-scrollbar">
              {isSuccess ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                    <SafeIcon icon="FiCheck" className="text-4xl" />
                  </div>
                  <h3 className="text-2xl font-bold">Reservation Confirmed!</h3>
                  <p className="text-gray-500">Your batch will be prepared this Thursday and ship next Monday.</p>
                </div>
              ) : cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <SafeIcon icon="FiInbox" className="text-6xl text-frost-ice-dark" />
                  <p className="text-gray-500 font-medium">Your box is currently empty.</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="text-frost-berry font-bold hover:underline"
                  >
                    Start Browsing
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex gap-4 bg-white p-4 rounded-2xl border border-frost-ice">
                      <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-xl" />
                      <div className="flex-grow">
                        <div className="flex justify-between items-start">
                          <h4 className="font-bold text-frost-dark leading-tight">{item.name}</h4>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            className="text-gray-400 hover:text-frost-berry"
                          >
                            <SafeIcon icon="FiTrash2" />
                          </button>
                        </div>
                        <p className="text-frost-berry font-bold mt-1">${item.price}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <button 
                            onClick={() => updateQuantity(item.id, -1)}
                            className="w-8 h-8 rounded-lg border border-frost-ice flex items-center justify-center hover:bg-frost-ice"
                          >
                            <SafeIcon icon="FiMinus" />
                          </button>
                          <span className="font-bold w-4 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-8 h-8 rounded-lg border border-frost-ice flex items-center justify-center hover:bg-frost-ice"
                          >
                            <SafeIcon icon="FiPlus" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && !isSuccess && (
              <div className="p-6 bg-white border-t border-frost-ice space-y-4">
                <div className="flex justify-between items-center text-lg">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-black text-frost-berry">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-frost-berry-light bg-frost-ice/30 p-3 rounded-xl">
                  <SafeIcon icon="FiTruck" />
                  <span>Ships frozen on Monday via Overnight Express</span>
                </div>
                <button 
                  onClick={handleCheckout}
                  disabled={isCheckingOut}
                  className="btn-primary w-full flex items-center justify-center gap-2 h-14"
                >
                  {isCheckingOut ? (
                    <>
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                      >
                        <SafeIcon icon="FiLoader" />
                      </motion.div>
                      Processing...
                    </>
                  ) : (
                    'Confirm Preorder'
                  )}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;